import { surahVerses, surahs, ayahPageNumbers } from '../constants/quranData';

/**
 * Get the surah number (1-114) from surah name
 */
const getSurahNumber = (surahName) => {
  const index = surahs.indexOf(surahName);
  return index !== -1 ? index + 1 : null;
};

/**
 * Get page number for a specific ayah
 * @param {string|number} surah - Surah name or surah number
 * @param {number} verse - Verse number
 * @returns {number|null} Page number or null if not found
 */
export const getAyahPageNumber = (surah, verse) => {
  if (!surah || !verse) return null;

  // Convert surah name to number if needed
  const surahNumber = typeof surah === 'string' ? getSurahNumber(surah) : surah;
  if (!surahNumber) return null;

  const key = `${surahNumber}:${verse}`;
  return ayahPageNumbers[key] || null;
};

/**
 * Check if a verse is the first verse on its page
 * @param {string|number} surah - Surah name or surah number
 * @param {number} verse - Verse number
 * @returns {boolean} True if this is the first verse on the page
 */
const isFirstVerseOnPage = (surah, verse) => {
  const page = getAyahPageNumber(surah, verse);
  if (page === null) return false;

  // Convert surah name to number if needed
  const surahNumber = typeof surah === 'string' ? getSurahNumber(surah) : surah;
  if (!surahNumber) return false;

  // Find all verses on this page
  const versesOnPage = [];
  for (const [key, pageNumber] of Object.entries(ayahPageNumbers)) {
    if (pageNumber === page) {
      const [surahNum, verseNum] = key.split(':').map(Number);
      versesOnPage.push({ surah: surahNum, verse: verseNum });
    }
  }

  if (versesOnPage.length === 0) return false;

  // Find the first verse on this page (lowest surah:verse combination)
  const firstVerse = versesOnPage.reduce((first, current) => {
    if (current.surah < first.surah) return current;
    if (current.surah === first.surah && current.verse < first.verse) return current;
    return first;
  });

  // Check if the given verse matches the first verse on the page
  return firstVerse.surah === surahNumber && firstVerse.verse === verse;
};

/**
 * Check if a verse is the last verse on its page
 * @param {string|number} surah - Surah name or surah number
 * @param {number} verse - Verse number
 * @returns {boolean} True if this is the last verse on the page
 */
const isLastVerseOnPage = (surah, verse) => {
  const page = getAyahPageNumber(surah, verse);
  if (page === null) return false;

  // Convert surah name to number if needed
  const surahNumber = typeof surah === 'string' ? getSurahNumber(surah) : surah;
  if (!surahNumber) return false;

  // Find all verses on this page
  const versesOnPage = [];
  for (const [key, pageNumber] of Object.entries(ayahPageNumbers)) {
    if (pageNumber === page) {
      const [surahNum, verseNum] = key.split(':').map(Number);
      versesOnPage.push({ surah: surahNum, verse: verseNum });
    }
  }

  if (versesOnPage.length === 0) return false;

  // Find the last verse on this page (highest surah:verse combination)
  const lastVerse = versesOnPage.reduce((last, current) => {
    if (current.surah > last.surah) return current;
    if (current.surah === last.surah && current.verse > last.verse) return current;
    return last;
  });

  // Check if the given verse matches the last verse on the page
  return lastVerse.surah === surahNumber && lastVerse.verse === verse;
};

/**
 * Calculate verses completed between two ayahs
 */
export const calculateVerses = (startSurah, startVerse, endSurah, endVerse) => {
  if (!startSurah || !endSurah || !startVerse || !endVerse) return 0;

  const surahsList = Object.keys(surahVerses);
  const startIndex = surahsList.indexOf(startSurah);
  const endIndex = surahsList.indexOf(endSurah);

  if (startIndex === -1 || endIndex === -1) return 0;

  // If same surah, just calculate difference
  if (startSurah === endSurah) {
    return endVerse - startVerse + 1;
  }

  // Calculate verses from multiple surahs
  let totalVerses = 0;

  // Add remaining verses from first surah
  totalVerses += surahVerses[startSurah] - startVerse + 1;

  // Add complete surahs in between
  for (let i = startIndex + 1; i < endIndex; i++) {
    totalVerses += surahVerses[surahsList[i]];
  }

  // Add verses from last surah
  totalVerses += endVerse;

  return totalVerses;
};

/**
 * Calculate pages completed between two ayahs
 * Only counts fully completed pages. 
 * - Start page is only counted if the starting verse is the first verse on that page
 * - End page is only counted if the ending verse is the last verse on that page
 * - All pages in between are counted as fully completed
 * @param {string} startSurah - Starting surah name
 * @param {number} startVerse - Starting verse number
 * @param {string} endSurah - Ending surah name
 * @param {number} endVerse - Ending verse number
 * @returns {number} Number of pages completed
 */
export const calculatePages = (startSurah, startVerse, endSurah, endVerse) => {
  if (!startSurah || !endSurah || !startVerse || !endVerse) return 0;

  const startPage = getAyahPageNumber(startSurah, startVerse);
  const endPage = getAyahPageNumber(endSurah, endVerse);

  // If we can't find page numbers, fall back to verses calculation
  if (startPage === null || endPage === null) {
    console.warn('Page numbers not found, falling back to verses calculation');
    return calculateVerses(startSurah, startVerse, endSurah, endVerse);
  }

  // If start and end are on the same page
  if (startPage === endPage) {
    // Only count if start is first verse AND end is last verse on that page
    return (isFirstVerseOnPage(startSurah, startVerse) && isLastVerseOnPage(endSurah, endVerse)) ? 1 : 0;
  }

  // Count fully completed pages between start and end (from startPage+1 to endPage-1)
  let completedPages = Math.max(0, endPage - startPage - 1);

  // Only count the start page if the start verse is the first verse on that page
  if (isFirstVerseOnPage(startSurah, startVerse)) {
    completedPages += 1;
  }

  // Only count the end page if the end verse is the last verse on that page
  if (isLastVerseOnPage(endSurah, endVerse)) {
    completedPages += 1;
  }

  return completedPages;
};

/**
 * Calculate progress (verses or pages) based on mode
 * @param {string} startSurah - Starting surah name
 * @param {number} startVerse - Starting verse number
 * @param {string} endSurah - Ending surah name
 * @param {number} endVerse - Ending verse number
 * @param {string} mode - 'verses' or 'pages'
 * @returns {number} Progress value
 */
export const calculateProgress = (startSurah, startVerse, endSurah, endVerse, mode = 'verses') => {
  if (mode === 'pages') {
    return calculatePages(startSurah, startVerse, endSurah, endVerse);
  }
  return calculateVerses(startSurah, startVerse, endSurah, endVerse);
};
